import { useLazyQuery, useMutation } from '@apollo/client'
import { TypedFormik } from '@dai/components'
import {
  CheckCircle,
  RadioButtonUnchecked,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { logAnalyticsEvent } from 'api/Firebase'
import {
  getAuth,
  PasswordPolicy,
  PasswordValidationStatus,
  validatePassword,
} from 'firebase/auth'
import { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import * as Yup from 'yup'

import {
  CLEAN_UP_USER,
  CleanUpUserMutation,
  CleanUpUserMutationVariables,
  GET_USER_BY_FIREBASE_ID,
  GetUserByFirebaseIdQuery,
  GetUserByFirebaseIdQueryVariables,
  UPDATE_USER,
  UpdateUserMutation,
  UpdateUserMutationVariables,
} from '@/graphql'
import useAuth from '@/hooks/useAuth'
import useT from '@/i18n/useT'
import TermsAndConditions from '@/pages/forms/terms-and-conditions/TermsAndConditions'

import LanguageSelector from '../common/LanguageSelector'

function CreateAccount({
  invitation,
  onSuccess,
}: {
  invitation?: {
    email: string
    tenantId: string
    groupIds: string[]
    storeIds: string[]
  }
  onSuccess?: () => void
}) {
  const { t } = useT()
  const { createUser, sendEmailVerification, signOut, setCreatingUser } =
    useAuth()

  const [termsDialogOpen, setTermsDialogOpen] = useState(false)
  const [termsHaveBeenOpened, setTermsHaveBeenOpened] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy | null>(
    null,
  )
  const [passwordStatus, setPasswordStatus] =
    useState<PasswordValidationStatus | null>(null)
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false)

  const auth = getAuth()
  const handleClickShowPassword = () => setShowPassword(show => !show)

  useEffect(() => {
    const fetchPasswordPolicy = async () => {
      try {
        const validation = await validatePassword(auth, '')
        setPasswordPolicy(validation.passwordPolicy)
      } catch (error) {
        console.warn('Could not fetch password policy:', error)
      }
    }
    fetchPasswordPolicy()
  }, [auth])

  const validatePasswordAgainstPolicy = async (password: string) => {
    try {
      const validation = await validatePassword(auth, password)
      setPasswordStatus(validation)
      if (!passwordPolicy) {
        setPasswordPolicy(validation.passwordPolicy)
      }
      return validation
    } catch (error) {
      console.warn('Password validation failed:', error)
      return null
    }
  }

  const [updateUser] = useMutation<
    UpdateUserMutation,
    UpdateUserMutationVariables
  >(UPDATE_USER)

  const [getUserByFirebaseId] = useLazyQuery<
    GetUserByFirebaseIdQuery,
    GetUserByFirebaseIdQueryVariables
  >(GET_USER_BY_FIREBASE_ID)

  const [cleanUpUser] = useMutation<
    CleanUpUserMutation,
    CleanUpUserMutationVariables
  >(CLEAN_UP_USER)

  const ERROR_MESSAGES: Record<string, string> = {
    'auth/invalid-email': t('createAccount.error.invalidEmail'),
    'auth/user-disabled': t('createAccount.error.userDisabled'),
    'auth/user-not-found': t('createAccount.error.userNotFound'),
    'auth/wrong-password': t('createAccount.error.wrongPassword'),
    'auth/email-already-in-use': t('createAccount.error.emailInUse'),
    'auth/too-many-requests': t('createAccount.error.tooManyRequests'),
  }

  const getPasswordRequirements = () => {
    if (!passwordPolicy) return []

    const requirements = []
    const { customStrengthOptions } = passwordPolicy

    if (customStrengthOptions.minPasswordLength) {
      requirements.push({
        key: 'minLength',
        text: t('createAccount.validation.passwordMinLength', {
          minLength: customStrengthOptions.minPasswordLength,
        }),
        met: passwordStatus?.meetsMinPasswordLength ?? false,
      })
    }

    if (customStrengthOptions.containsLowercaseLetter) {
      requirements.push({
        key: 'lowercase',
        text: t('createAccount.validation.passwordLowercase'),
        met: passwordStatus?.containsLowercaseLetter ?? false,
      })
    }

    if (customStrengthOptions.containsUppercaseLetter) {
      requirements.push({
        key: 'uppercase',
        text: t('createAccount.validation.passwordUppercase'),
        met: passwordStatus?.containsUppercaseLetter ?? false,
      })
    }

    if (customStrengthOptions.containsNumericCharacter) {
      requirements.push({
        key: 'numeric',
        text: t('createAccount.validation.passwordNumeric'),
        met: passwordStatus?.containsNumericCharacter ?? false,
      })
    }

    if (customStrengthOptions.containsNonAlphanumericCharacter) {
      requirements.push({
        key: 'special',
        text: t('createAccount.validation.passwordSpecial'),
        met: passwordStatus?.containsNonAlphanumericCharacter ?? false,
      })
    }

    return requirements
  }

  return (
    <>
      <TypedFormik
        initialValues={{
          firstName: '',
          lastName: '',
          email: invitation?.email ?? '',
          companyName: '',
          password: '',
          confirmPassword: '',
          termsAccepted: false,
        }}
        validationSchema={Yup.object().shape({
          firstName: Yup.string()
            .max(255)
            .required(t('createAccount.validation.firstNameRequired')),
          lastName: Yup.string()
            .max(255)
            .required(t('createAccount.validation.lastNameRequired')),
          email: Yup.string()
            .email(t('createAccount.validation.invalidEmail'))
            .max(255)
            .required(t('createAccount.validation.emailRequired')),
          companyName: invitation
            ? Yup.string().max(100)
            : Yup.string()
                .max(100)
                .required(t('createAccount.validation.companyNameRequired')),
          password: Yup.string()
            .required(t('createAccount.validation.passwordRequired'))
            .test(
              'firebase-validation',
              t('createAccount.validation.passwordFirebaseRequirements'),
              value => {
                if (!value || !passwordStatus) return true
                return passwordStatus.isValid
              },
            ),
          confirmPassword: Yup.string().oneOf(
            [Yup.ref('password')],
            t('createAccount.validation.passwordMatch'),
          ),
          termsAccepted: Yup.bool()
            .oneOf([true], t('createAccount.validation.termsAccepted'))
            .required(t('createAccount.validation.termsRequired')),
        })}
        onSubmit={async (values, { setStatus }) => {
          try {
            setCreatingUser(true)

            // Final password validation before submission
            const finalValidation = await validatePasswordAgainstPolicy(
              values.password,
            )
            if (finalValidation && !finalValidation.isValid) {
              throw new Error(
                t('createAccount.validation.passwordFirebaseRequirements'),
              )
            }

            // Create a Firebase user account
            const firebaseUser = await createUser(
              values.email,
              values.password,
              values.firstName,
              values.lastName,
            )

            const firebaseUid = firebaseUser.uid

            let userId = null
            try {
              // Get the user created by Firebase middleware in Mercury (swiss-army)
              const { data: userData } = await getUserByFirebaseId({
                variables: {
                  firebaseId: firebaseUid,
                },
              })
              userId = userData?.userByFirebaseId?.id

              if (!userId) {
                throw new Error('Error creating user. User not found.')
              }

              // update user in Mercury
              await updateUser({
                variables: {
                  data: {
                    id: userId,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    username: values.email,
                    email: values.email,
                    ...(invitation
                      ? {
                          tenantId: invitation.tenantId,
                          groupIds: invitation.groupIds,
                          storeIds: invitation.storeIds,
                        }
                      : {
                          tenant: {
                            name: values.companyName,
                          },
                        }),
                  },
                },
              })

              await sendEmailVerification()

              setStatus({ success: true })
              onSuccess?.()
              logAnalyticsEvent('sign_up_complete')
            } catch (error) {
              try {
                // If any step after Firebase account creation fails,
                // we need to fully clean up the user to avoid the
                // "Error creating user. User not found." error on retry
                try {
                  if (userId) {
                    await cleanUpUser({
                      variables: {
                        input: {
                          firebaseId: firebaseUid,
                          userId,
                        },
                      },
                    })
                  }
                } catch (cleanupError) {
                  console.error('Failed to clean up user:', cleanupError)
                } finally {
                  await firebaseUser.delete()
                  await signOut()
                }
              } catch (deleteError) {
                console.error(
                  'Failed to delete and clean up user:',
                  deleteError,
                )
              }
              console.error('Error creating user:', error)
              throw new Error(t('createAccount.error.userCreationMessage'))
            }
          } catch (error: any) {
            const message =
              ERROR_MESSAGES[error.code] ||
              error.message ||
              t('common.somethingWentWrong')
            setStatus({ success: false, message })
          } finally {
            setCreatingUser(false)
          }
        }}
      >
        {({
          status,
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          setValues,
        }) => {
          const EmailField = (
            <TextField
              id="field-3"
              type="email"
              name="email"
              disabled={!!invitation}
              label={t('createAccount.email')}
              value={values.email}
              error={Boolean(touched.email && errors.email)}
              fullWidth
              helperText={touched.email && errors.email}
              onBlur={handleBlur}
              onChange={handleChange}
            />
          )

          const passwordRequirements = getPasswordRequirements()

          return (
            <form noValidate onSubmit={handleSubmit}>
              <Stack spacing={4}>
                {status?.message && (
                  <Alert severity={status.success ? 'success' : 'warning'}>
                    {status.message}
                  </Alert>
                )}
                <TextField
                  id="field-1"
                  type="text"
                  name="firstName"
                  label={t('createAccount.firstName')}
                  value={values.firstName}
                  error={Boolean(touched.firstName && errors.firstName)}
                  fullWidth
                  helperText={touched.firstName && errors.firstName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  sx={{
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow:
                        '0 0 0 1000px transparent inset !important',
                      backgroundColor: 'transparent !important',
                      WebkitTextFillColor: 'inherit !important',
                    },
                    '& input:-webkit-autofill:hover': {
                      WebkitBoxShadow:
                        '0 0 0 1000px transparent inset !important',
                      backgroundColor: 'transparent !important',
                    },
                    '& input:-webkit-autofill:focus': {
                      WebkitBoxShadow:
                        '0 0 0 1000px transparent inset !important',
                      backgroundColor: 'transparent !important',
                    },
                  }}
                />
                <TextField
                  id="field-2"
                  type="text"
                  name="lastName"
                  label={t('createAccount.lastName')}
                  value={values.lastName}
                  error={Boolean(touched.lastName && errors.lastName)}
                  fullWidth
                  helperText={touched.lastName && errors.lastName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  sx={{
                    '& input:-webkit-autofill': {
                      WebkitBoxShadow:
                        '0 0 0 1000px transparent inset !important',
                      backgroundColor: 'transparent !important',
                      WebkitTextFillColor: 'inherit !important',
                    },
                    '& input:-webkit-autofill:hover': {
                      WebkitBoxShadow:
                        '0 0 0 1000px transparent inset !important',
                      backgroundColor: 'transparent !important',
                    },
                    '& input:-webkit-autofill:focus': {
                      WebkitBoxShadow:
                        '0 0 0 1000px transparent inset !important',
                      backgroundColor: 'transparent !important',
                    },
                  }}
                />
                {invitation ? (
                  <Tooltip
                    title={t('createAccount.disabledEmailTooltip', {
                      email: values.email,
                    })}
                  >
                    {EmailField}
                  </Tooltip>
                ) : (
                  EmailField
                )}
                {!invitation && (
                  <TextField
                    id="field-4"
                    type="text"
                    name="companyName"
                    label={t('createAccount.companyName')}
                    value={values.companyName}
                    error={Boolean(touched.companyName && errors.companyName)}
                    fullWidth
                    helperText={touched.companyName && errors.companyName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                )}

                <TextField
                  id="field-5"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  label={t('createAccount.password')}
                  value={values.password}
                  error={Boolean(touched.password && errors.password)}
                  fullWidth
                  helperText={touched.password && errors.password}
                  onBlur={e => {
                    handleBlur(e)
                    setShowPasswordRequirements(false)
                  }}
                  onChange={async e => {
                    handleChange(e)
                    // Validate password in real-time
                    if (e.target.value) {
                      await validatePasswordAgainstPolicy(e.target.value)
                      setShowPasswordRequirements(true)
                    } else {
                      setPasswordStatus(null)
                      setShowPasswordRequirements(false)
                    }
                  }}
                  onFocus={() => {
                    setShowPasswordRequirements(true)
                  }}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                            aria-label="toggle password visibility"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                {showPasswordRequirements &&
                  passwordRequirements.length > 0 && (
                    <Box
                      sx={{
                        p: 3,
                        bgcolor: 'background.default',
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {t('createAccount.validation.passwordRequirements')}
                      </Typography>
                      <List dense sx={{ py: 0 }}>
                        {passwordRequirements.map(req => (
                          <ListItem key={req.key} sx={{ py: 0.25 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              {req.met ? (
                                <CheckCircle color="success" fontSize="small" />
                              ) : (
                                <RadioButtonUnchecked
                                  color="disabled"
                                  fontSize="small"
                                />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={req.text}
                              primaryTypographyProps={{
                                variant: 'body2',
                                color: req.met ? 'success' : 'text.secondary',
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                <TextField
                  id="field-6"
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  label={t('createAccount.confirmPassword')}
                  value={values.confirmPassword}
                  error={Boolean(
                    touched.confirmPassword && errors.confirmPassword,
                  )}
                  fullWidth
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
                <FormControl
                  error={Boolean(touched.termsAccepted && errors.termsAccepted)}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="termsAccepted"
                        checked={values.termsAccepted}
                        onChange={e => {
                          if (!termsHaveBeenOpened) {
                            setTermsDialogOpen(true)
                            return
                          }
                          handleChange(e)
                        }}
                        color="primary"
                      />
                    }
                    label={
                      <Typography
                        onClick={e => e.preventDefault()}
                        sx={{ cursor: 'default' }}
                      >
                        <Link
                          sx={{ cursor: 'pointer' }}
                          onClick={e => {
                            e.preventDefault()
                            setTermsDialogOpen(true)
                          }}
                        >
                          {t('createAccount.termsText')}
                        </Link>
                      </Typography>
                    }
                  />
                  {touched.termsAccepted && errors.termsAccepted && (
                    <FormHelperText>{errors.termsAccepted}</FormHelperText>
                  )}
                </FormControl>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                  loadingPosition="end"
                >
                  {t('createAccount.signUp')}
                </Button>
                <Typography sx={{ textAlign: 'center' }}>
                  {t('createAccount.haveAccountText')}{' '}
                  <Link to="/auth/sign-in" component={RouterLink}>
                    {t('createAccount.loginLink')}
                  </Link>
                </Typography>
              </Stack>
              <Dialog
                open={termsDialogOpen}
                onClose={() => setTermsDialogOpen(false)}
              >
                <DialogTitle variant="h2">
                  {t('createAccount.termsTitle')}
                </DialogTitle>
                <DialogContent
                  sx={{
                    px: 6,
                  }}
                >
                  <Box
                    sx={{
                      height: '50vh',
                      overflow: 'auto',
                      border: '1px solid lightgray',
                      p: 6,
                    }}
                    onScroll={e => {
                      const element = e.currentTarget
                      if (
                        element.scrollHeight - element.scrollTop <=
                        element.clientHeight + 3 // 3px buffer
                      ) {
                        setTermsHaveBeenOpened(true)
                      }
                    }}
                  >
                    <TermsAndConditions />
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setTermsDialogOpen(false)}>
                    {t('createAccount.close')}
                  </Button>
                  <Button
                    onClick={() => {
                      setValues(prev => ({ ...prev, termsAccepted: true }))
                      setTermsDialogOpen(false)
                    }}
                    disabled={!termsHaveBeenOpened}
                  >
                    {t('createAccount.agreeAndClose')}
                  </Button>
                </DialogActions>
              </Dialog>
            </form>
          )
        }}
      </TypedFormik>
      <LanguageSelector />
    </>
  )
}

export default CreateAccount
